import { useEffect, useState } from 'react'

import axios from 'axios'

import i18n from 'modules/core/lib/i18n'
import { parseErrorData } from 'modules/core/lib/parseErrorData'
import app from 'modules/core/store/models/app'

/**
 * FETCH YOUR DATA AND DON'T MANAGE LOADING STATE
 * :::::::::::::::::::::::::::::::::::::::::::::::
 *
 * This hook is convenient to fetch data from our models/collections, or from another endpoint.
 * It avoid using always `useEffect + useState` to handle data loading state
 * You can use like this:
 *
 * Single entity:
 *
 *  const { loading } = useAsyncData(() => myModel.fetch())
 *
 * Multiple entities:
 *
 *  const { loading } = useAsyncData(() =>
 *    Promise.all([
 *      employees.fetch(),
 *      myThingsCollection.fetch({ data: { user_id: current.get('id') } ),
 *      myModel.fetch(),
 *    ])
 *  , [])
 *
 * With dependencies:
 *
 *   const { loading } = useAsyncData(() => (
 *     user.fetch({ year })
 *    ), [year])
 *
 * Another endpoint, using value:
 *
 *   const { loading, value } = useAsyncData(() => (
 *    apiClient().get('/my_custom_endpoint')
 *   ))
 *
 *   const name = value?.name
 *
 * With error handling:
 *
 *   const { loading, error } = useAsyncData(() => myModel.fetch(), [], {
 *     errorFlash: true
 *   })
 *
 */
type Value<S> = S | void | null | undefined

/**
 * Options for error handling in useAsyncData
 */
export interface UseAsyncDataOptions {
  /**
   * Whether to automatically show a flash message when an error occurs
   */
  errorFlash?: boolean
  /**
   * Custom error message to display instead of parsing from the error response
   */
  errorMessage?: string
}

interface UseAsyncDataType<S> {
  loading: boolean
  value: Value<S>
  error?: Error
}

export type Scalar = boolean | number | string | bigint | null | undefined

export function useAsyncData<S>(
  action: () => Promise<Value<S>>,
  dependencies: Scalar[],
  options?: UseAsyncDataOptions
): UseAsyncDataType<S> {
  const [loading, setLoading] = useState(true)
  const [value, setValue] = useState<Value<S>>()
  const [error, setError] = useState<Error | undefined>(undefined)

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      try {
        const result = await action()
        setValue(result)
      } catch (err) {
        const errorInstance = err instanceof Error ? err : new Error(String(err))
        setError(errorInstance)

        if (options?.errorFlash) {
          let errorMessage = options.errorMessage || i18n.t('errors.generic_error')

          if (axios.isAxiosError(err) && err.response?.data) {
            const parsedError = parseErrorData(err.response.data)
            if (parsedError) {
              errorMessage = parsedError
            }
          }

          app.setFlash({
            type: 'wrong',
            message: errorMessage,
            time: 3000,
          })
        }
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, dependencies) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    loading,
    value,
    error,
  }
}

export function useUnsafeAsyncData<S>(
  action: () => Promise<S>,
  dependencies: any[],
  options?: UseAsyncDataOptions
): UseAsyncDataType<S> {
  return useAsyncData(action, dependencies, options)
}
